<script lang="ts">
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import HoverButton from "../HoverMenu/HoverButton.svelte";
  import {
    CircleInfo,
    Exclamation,
    ExclamationTriangle,
    LightBulb,
  } from "$components/+blocks/OVIcon";
  import { NodeViewWrapper, NodeViewContent } from "svelte-tiptap";
  import GutterMenu from "$components/editor/extensions/+shared/GutterMenu.svelte";
  import type { NodeViewProps } from "@tiptap/core";

  export let node: NodeViewProps["node"];
  export let updateAttributes: NodeViewProps["updateAttributes"];
  export let deleteNode: NodeViewProps["deleteNode"];
  export let isEditable: boolean;

  type Variant = "default" | "info" | "warning" | "danger" | "success";

  $$restProps; // consume unused tiptap props
  let variant: Variant = node.attrs.variant;

  function updateVariant(newVariant: Variant) {
    updateAttributes({ variant: newVariant });
    variant = newVariant;
  }

  const icons = {
    default: CircleInfo,
    info: CircleInfo,
    warning: Exclamation,
    danger: ExclamationTriangle,
    success: LightBulb,
  };
  const bgColours = {
    default: "bg-black/5 dark:bg-white/5 border-black/30 dark:border-white/30",
    info: "bg-sky-400/5 dark:bg-sky-400/4 border-sky-400/30 ",
    warning: "bg-amber-400/5  border-amber-400/50 dark:border-amber-400/30",
    danger: "bg-red-400/5 border-red-400/50 dark:border-red-400/30",
    success: "bg-green-400/5 border-green-400/50 dark:border-green-400/30",
  };
  const textColours = {
    default: "text-neutral-600 dark:text-neutral-300",
    info: "text-sky-900 dark:text-sky-100",
    warning: "text-amber-900 dark:text-amber-100",
    danger: "text-red-900 dark:text-red-100",
    success: "text-green-900 dark:text-green-100",
  };
  const hoverIconColours = {
    default: "text-neutral-600 dark:text-neutral-300",
    info: "text-sky-700 dark:text-sky-300",
    warning: "text-amber-700 dark:text-amber-300",
    danger: "text-red-700 dark:text-red-300",
    success: "text-green-700 dark:text-green-300",
  };
</script>

<NodeViewWrapper class="group relative">
  {#if isEditable}
    <GutterMenu on:delete-block={deleteNode} />
    <div
      class="absolute inset-x-0 bottom-0 z-[999] mb-2 hidden translate-y-full justify-center group-hover:flex"
    >
      <div
        class="flex flex-col gap-y-1 rounded-lg border border-neutral-300 bg-white p-1 text-sm dark:border-neutral-600 dark:bg-neutral-700"
      >
        <div class="inline-flex gap-1">
          <HoverButton active={variant == "default"} on:click={() => updateVariant("default")}>
            <OVIcon class={hoverIconColours["default"]} icon={icons["default"]} />
          </HoverButton>
          <HoverButton active={variant == "info"} on:click={() => updateVariant("info")}>
            <OVIcon class={hoverIconColours["info"]} icon={icons["info"]} />
          </HoverButton>
          <HoverButton active={variant == "warning"} on:click={() => updateVariant("warning")}>
            <OVIcon class={hoverIconColours["warning"]} icon={icons["warning"]} />
          </HoverButton>
          <HoverButton active={variant == "danger"} on:click={() => updateVariant("danger")}>
            <OVIcon class={hoverIconColours["danger"]} icon={icons["danger"]} />
          </HoverButton>
          <HoverButton active={variant == "success"} on:click={() => updateVariant("success")}>
            <OVIcon class={hoverIconColours["success"]} icon={icons["success"]} />
          </HoverButton>
        </div>
      </div>
    </div>
  {/if}

  <div
    class="my-5 flex items-start gap-2 rounded-xl border px-4 after:rounded-md dark:border-[0.5px] {bgColours[
      variant
    ]} transition-colours"
  >
    <span class="mt-4 pt-1">
      <OVIcon icon={icons[variant]} class={textColours[variant]} />
    </span>
    <span class="text-sm leading-6 font-medium dark:font-normal {textColours[variant]}">
      <NodeViewContent />
    </span>
  </div>
</NodeViewWrapper>
